import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useGetServiceUsersQuery,
  useGetDashboardLocationsQuery,
  formatValue,
  type DashboardOptions
} from '@/entities/dashboard';
import { useGetOrganizationsQuery, type Organization } from '@/entities/organizations';

import { useUrlState, useProjectSiteParams } from '@/shared/lib';
import type { DateRangeValue, FilterGroup } from '@/shared/ui';

import type { ServiceUsersUrlState, FormattedStats } from './types';

const DEFAULT_URL_STATE: ServiceUsersUrlState = {
  metric: 'W',
  period: 'L6M',
  fromDate: '',
  toDate: ''
};

export function useServiceUsers() {
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [organizationsSearchTerm, setOrganizationsSearchTerm] = useState('');

  const { data: locationsData, isLoading: locationsLoading } = useGetDashboardLocationsQuery(
    {
      projectId
    },
    { skip: !projectId }
  );

  const { data: organizationsData, isLoading: organizationsLoading } = useGetOrganizationsQuery({
    search: organizationsSearchTerm,
    projectId
  });

  const organizationGroups: FilterGroup[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    const groupedByType = new Map<string, Organization[]>();

    organizationsData.organizations.forEach((org) => {
      const typeDescription = org.orgType?.description || 'Other';
      if (!groupedByType.has(typeDescription)) {
        groupedByType.set(typeDescription, []);
      }
      groupedByType.get(typeDescription)!.push(org);
    });

    return Array.from(groupedByType.entries()).map(([typeDescription, orgs]) => ({
      group: typeDescription,
      groupId: typeDescription.toLowerCase().replace(/\s+/g, '-'),
      items: orgs.map((org) => ({
        id: org.id.toString(),
        name: `${org.name} (${org.abbreviation})`
      }))
    }));
  }, [organizationsData?.organizations]);

  const queryParams = useMemo(() => {
    return {
      ...urlState,
      projectId,
      siteId,
      locIds: selectedLocations.length > 0 ? selectedLocations : undefined,
      partners: selectedOrganizations.length > 0 ? selectedOrganizations : undefined
    };
  }, [urlState, projectId, siteId, selectedLocations, selectedOrganizations]);

  const { data, isLoading, isFetching, error } = useGetServiceUsersQuery(queryParams);

  const updateOption = useCallback(
    (key: keyof DashboardOptions, value: string | string[]) => {
      setUrlState({ [key]: value });
    },
    [setUrlState]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as ServiceUsersUrlState);
    },
    [setUrlState]
  );

  const formattedStats: FormattedStats | null = useMemo(() => {
    if (!data) return null;

    const netBalance = Math.abs(data.totalReceived - data.totalReleased);
    const utilizationRate =
      data.totalReceived > 0 ? ((data.totalReleased / data.totalReceived) * 100).toFixed(1) : '0.0';

    return {
      totalReceived: formatValue(data.totalReceived, urlState.metric),
      totalReleased: formatValue(data.totalReleased, urlState.metric),
      servicePartners: data.serviceUsers.categories.length.toString(),
      netBalance: formatValue(netBalance, urlState.metric),
      netBalancePositive: data.totalReceived > data.totalReleased,
      utilizationRate: `${utilizationRate}%`,
      activePartners: data.serviceUsers.categories.length,
      productTypes: data.topProducts.series.length
    };
  }, [data, urlState.metric]);

  useEffect(() => {
    setSelectedOrganizations([]);
  }, [projectId]);

  return {
    // Data
    data,
    formattedStats,
    locationsData,
    organizationGroups,

    // Loading states
    isLoading,
    isFetching,
    locationsLoading,
    organizationsLoading,
    error,

    // Filters
    urlState,
    selectedLocations,
    selectedOrganizations,
    organizationsSearchTerm,

    // Handlers
    updateOption,
    handleDateRangeChange,
    setSelectedLocations,
    setSelectedOrganizations,
    setOrganizationsSearchTerm,
    resetFilters: resetUrlState
  };
}
